import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import { IconCheck, IconX } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { ResolutionTierBadge } from '../components/ResolutionTierBadge';
import {
  getResolutionAutomatedLabel,
  getResolutionTierLabel,
} from '../constants';
import { InlineData } from './InlineData';
import { ResolutionTierInsights } from 'src/services/insights/types';

export const ResolutionSummaryData = ({
  deflected,
  resolutionAutomated,
  resolutionTier,
  resolutionTierInsights,
}: {
  deflected?: boolean | null;
  resolutionTier?: string | null;
  resolutionAutomated?: boolean | null;
  resolutionTierInsights?: ResolutionTierInsights | null;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  // Prefer the tier attached to the insights payload when present; it is
  // generated with the same signal reasoning shown in this section.
  const tier =
    resolutionTierInsights?.resolution_tier ?? resolutionTier ?? undefined;

  return (
    <>
      <InlineData
        label={t('resolutionTierInsightsData.automated')}
        value={getResolutionAutomatedLabel(resolutionAutomated)}
      />
      {typeof deflected === 'boolean' && (
        <InlineData
          label={t('drawerBody.deflectedLabel')}
          value={
            deflected
              ? t('drawerBody.deflectedTrue')
              : t('drawerBody.deflectedFalse')
          }
        />
      )}
      <InlineData
        label={t('filterOptions.resolved')}
        value={getResolutionTierLabel(tier)}
      />
    </>
  );
};

export const ResolutionTierInsightsData = ({
  resolutionTier,
  resolutionTierInsights,
}: {
  resolutionTier?: string | null;
  resolutionTierInsights?: ResolutionTierInsights | null;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const tier =
    resolutionTierInsights?.resolution_tier ?? resolutionTier ?? undefined;

  if (!tier && !resolutionTierInsights) {
    return null;
  }
  const reasoning = resolutionTierInsights?.customer_issue_solved_reasoning;
  const signals = getSignals(t, resolutionTierInsights);

  return (
    <ResolutionSection>
      <Typography color={palette.colors.grey[600]} variant='font12Medium'>
        {t('resolutionTierInsightsData.resolution')}
      </Typography>
      <ResolutionDetails>
        <ResolutionBadgeWrapper>
          <ResolutionTierBadge resolutionTier={tier} />
        </ResolutionBadgeWrapper>
        {reasoning && (
          <Typography color={palette.colors.grey[600]} variant='font12'>
            {reasoning}
          </Typography>
        )}
        {signals.length > 0 && (
          <SignalList>
            {signals.map(({ isPositive, label }) => (
              <SignalItem key={label}>
                <SignalIcon $isPositive={isPositive} aria-hidden='true'>
                  {isPositive ? (
                    <IconCheck size={16} stroke={3} />
                  ) : (
                    <IconX size={16} stroke={3} />
                  )}
                </SignalIcon>
                <Typography
                  color={palette.colors.slate[900]}
                  variant='font12Medium'
                >
                  {label}
                </Typography>
              </SignalItem>
            ))}
          </SignalList>
        )}
      </ResolutionDetails>
    </ResolutionSection>
  );
};

const getSignals = (
  t: TFunction<'intent-conversation-analytics'>,
  resolutionTierInsights?: ResolutionTierInsights | null,
) => {
  if (!resolutionTierInsights) {
    return [];
  }

  return [
    {
      isPositive: Boolean(resolutionTierInsights.customer_has_a_request),
      label: t('resolutionTierInsightsData.requestDetected'),
    },
    {
      isPositive: Boolean(resolutionTierInsights.customer_issue_solved),
      label: t('resolutionTierInsightsData.issueSolved'),
    },
    {
      isPositive: Boolean(
        resolutionTierInsights.user_leaves_chat_without_confirming_helpfulness,
      ),
      label: t('resolutionTierInsightsData.leftWithoutConfirming'),
    },
  ];
};

const ResolutionSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ResolutionDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ResolutionBadgeWrapper = styled(Box)`
  align-self: flex-start;
`;

const SignalList = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding-top: 4px;
`;

const SignalItem = styled(Box)`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const SignalIcon = styled('span')<{ $isPositive: boolean }>`
  color: ${({ $isPositive, theme }) =>
    $isPositive
      ? theme.palette.colors.green[400]
      : theme.palette.colors.red[500]};
  display: inline-flex;
`;
